import baustein from "baustein";
import PropTypes from "prop-types";
import React from "react";

class Baustein extends React.PureComponent {
    constructor(props) {
        super(props);

        this.sanityCheckProps(props);

        /** @type React.RefObject<HTMLDivElement> */
        this.el = React.createRef();
        /** @type baustein.Component | undefined */
        this.binstance = undefined;
        /** @type baustein.Component[] */
        this.subBintances = [];
        /** @type HTMLElement[] */
        this.templateContents = [];

        this.state = {
            hasStarted: false,
        };
    }

    sanityCheckProps(props) {
        if (!(props.template || props.component)) {
            throw new Error("Must specify template or component!");
        }
    }

    componentDidMount() {
        // Extract our precious children
        if (!this.state.hasStarted && this.props.template && this.el.current) {
            let el = this.el.current;
            while (el.firstChild) {
                this.templateContents.push(el.removeChild(el.firstChild));
            }
        }

        // Tell React to render an empty div from now on
        this.setState({ hasStarted: true });
    }

    componentWillUnmount() {
        this.destroyOldComponent();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.hasStarted != prevState.hasStarted) {
            // First pass, let's go
            this.createNewComponent();
        } else if (
            this.props.template != prevProps.template ||
            this.props.component != prevProps.component
        ) {
            // Our component has changed, make a new one
            this.sanityCheckProps(this.props);
            this.destroyOldComponent();
            this.createNewComponent();
        } else {
            // Our `args` prop has changed, tell baustein
            this.updateCurrentComponent();
        }
    }

    destroyOldComponent() {
        if (this.binstance) {
            this.binstance.destroy();
            this.binstance = undefined;
        }
        this.subBintances.forEach((binstance) => binstance.destroy());
        this.subBintances = [];
    }

    createNewComponent() {
        let el = this.el.current;
        if (!el) {
            throw new Error("This function can only be called after rendering has taken place!");
        }
        let { template, component, args } = this.props;

        // If we have an actual component, it'll entirely take care of its own DOM.
        // Otherwise, we'll need to create the HTML and then sideload a component into the existing Baustein structure.
        if (component) {
            this.binstance = new component(args);
            this.binstance.appendTo(el);
            return;
        }

        // If we've got a SSR version of the template, use that instead of making our own
        if (this.templateContents.length > 0) {
            this.templateContents.forEach((node) => el.appendChild(node));
            this.templateContents = [];
        } else {
            /** @type string */
            let tempHTML = template(args).trim();
            if (tempHTML === "") {
                // This is either a catastrophic error or intentional. Hopefully it's intentional
                console.warn("Blank template rendered by", template, "with args", args, "?");
                return;
            }
            el.innerHTML = tempHTML;
        }

        // Normally, the jinja template should render a single element, which is a baustein component
        let rootComponent;
        if (el.children.length == 1) {
            rootComponent = baustein.fromElement(el.children[0]);
        }

        // If it hasn't done that, it's presumably rendered a tree of components.
        // We can't really manange those, but let's keep track of them regardless
        let subComponents = [];
        if (!rootComponent) {
            subComponents = baustein.parse(el);
        }

        // This will happen if the template renders nothing but text, or something big is broken
        if (!rootComponent && subComponents.length == 0) {
            throw new Error("No Baustein components in the rendered template!");
        }

        this.binstance = rootComponent;
        this.subBintances = subComponents;
    }

    updateCurrentComponent() {
        if (this.binstance) {
            this.binstance.updateOptions(this.props.args);
        }
    }

    render() {
        /*
            In order to have the rendered HTML available for SEO we need to
            render the template on the server which unfortunately means React
            has to know about its contents.
            In order to avoid vdom diffing further up the line trashing
            everything, we need to render the template (both on the server
            and client) and then detach the nodes manually, render an empty
            div and then re-attach them invisibly.
            As long as we only ever tell React that we have an empty div, it
            will never go check what's actually there.
        */
        if (!this.state.hasStarted && this.props.template) {
            let { template, args } = this.props;
            return (
                <div
                    suppressHydrationWarning={true}
                    className={this.props.className}
                    ref={this.el}
                    key="unique"
                    dangerouslySetInnerHTML={{ __html: template(args).trim() }}
                />
            );
        }
        return <div className={this.props.className} ref={this.el} key="unique" />;
    }
}

Baustein.propTypes = {
    args: PropTypes.object.isRequired,
    className: PropTypes.string,
    component: PropTypes.func,
    template: PropTypes.func,
};

export default Baustein;
